import React from 'react';
import Card from '../components/UI/Card';
import CodeSnippet from '../components/CodeSnippet';
import { API_ENDPOINTS, API_LIMITS, USER_ROLES } from '../utils/constants';

const Docs = () => {
  const authExample = `// Отримання токену
POST /auth/token
Content-Type: application/json

{
  "username": "your@email.com",
  "password": "your_password"
}

// Відповідь
{
  "access_token": "eyJhbGciOiJSUzI1NiIs...",
  "expires_in": 1800,
  "token_type": "Bearer",
  "refresh_token": "eyJhbGciOiJSUzI1NiIs..."
}`;

  const debtExample = `GET /api/v1/lakehouse/debt/debt?debtor_name=Іванов&limit=20&sort_by=debtor_name&order=asc`;

  const enforcementExample = `GET /api/v1/lakehouse/enforcement/enforcement?dvs_code=123&limit=20&cursor=eyJpZCI6MTIzfQ`;

  const courtExample = `GET /api/v1/lakehouse/court-documents/court-cases?cause_num=123/456&limit=20`;

  return (
    <div className="py-12">
      <div className="container-custom">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4">Документація API</h1>
          <p className="text-xl text-gray-600">
            REST API для доступу до структурованих даних про судові рішення, реєстр боржників та виконавчі провадження
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card>
                <div className="p-4 space-y-2">
                  <a href="#auth" className="block text-gray-700 hover:text-primary-600 py-2">Автентифікація</a>
                  <a href="#debt" className="block text-gray-700 hover:text-primary-600 py-2">Боржники (debt)</a>
                  <a href="#enforcement" className="block text-gray-700 hover:text-primary-600 py-2">Виконавчі провадження</a>
                  <a href="#court" className="block text-gray-700 hover:text-primary-600 py-2">Судові рішення</a>
                  <a href="#pagination" className="block text-gray-700 hover:text-primary-600 py-2">Пагінація</a>
                  <a href="#filters" className="block text-gray-700 hover:text-primary-600 py-2">Фільтрація</a>
                  <a href="#errors" className="block text-gray-700 hover:text-primary-600 py-2">Помилки</a>
                  <a href="#roles" className="block text-gray-700 hover:text-primary-600 py-2">Ролі</a>
                </div>
              </Card>
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3 space-y-8">
            {/* Authentication */}
            <section id="auth">
              <h2 className="text-2xl font-bold mb-4">Автентифікація</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    API використовує JWT токени. Токен діє <strong className="text-primary-600">30 хвилин</strong>.
                    Для отримання токену використовуйте ендпоїнт <code className="bg-gray-100 px-2 py-1 rounded">/auth/token</code>.
                  </p>
                  <CodeSnippet code={authExample} />
                  <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      <strong>Важливо:</strong> Передавайте токен в заголовку <code className="bg-yellow-100 px-1 py-0.5 rounded">Authorization: Bearer &lt;token&gt;</code>
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Debt records */}
            <section id="debt">
              <h2 className="text-2xl font-bold mb-4">Боржники (Реєстр боржників)</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    Ендпоїнт <code className="bg-gray-100 px-2 py-1 rounded">/api/v1/lakehouse/debt/debt</code> надає доступ до записів Єдиного реєстру боржників.
                  </p>
                  <CodeSnippet code={debtExample} />
                  <div className="mt-4 text-sm text-gray-600">
                    <p><strong>Параметри фільтрації:</strong> <code>debtor_code</code>, <code>debtor_birthdate</code>, <code>debtor_name</code> (повнотекстовий), <code>vp_ordernum</code>.</p>
                    <p><strong>Сортування:</strong> <code>sort_by=debt_id|debtor_name</code>, <code>order=asc|desc</code> (повнотекстовий пошук ігнорує сортування).</p>
                    <p><strong>Пагінація:</strong> використовуйте <code>page/limit</code> (max 200) або <code>cursor</code>.</p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Enforcement records */}
            <section id="enforcement">
              <h2 className="text-2xl font-bold mb-4">Виконавчі провадження</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    Ендпоїнт <code className="bg-gray-100 px-2 py-1 rounded">/api/v1/lakehouse/enforcement/enforcement</code> надає доступ до Автоматизованої системи виконавчого провадження.
                  </p>
                  <CodeSnippet code={enforcementExample} />
                  <div className="mt-4 text-sm text-gray-600">
                    <p><strong>Параметри фільтрації:</strong> <code>dvs_code</code>, <code>vp_ordernum</code>, <code>debtor_code</code>, <code>creditor_code</code>, <code>debtor_birthdate</code>, <code>debtor_name</code> (повнотекстовий).</p>
                    <p><strong>Сортування:</strong> <code>sort_by=enforcement_id|debtor_name</code>, <code>order=asc|desc</code>.</p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Court documents */}
            <section id="court">
              <h2 className="text-2xl font-bold mb-4">Судові рішення</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    Ендпоїнт <code className="bg-gray-100 px-2 py-1 rounded">/api/v1/lakehouse/court-documents/court-cases</code> надає доступ до Єдиного державного реєстру судових рішень.
                  </p>
                  <CodeSnippet code={courtExample} />
                  <div className="mt-4 text-sm text-gray-600">
                    <p><strong>Параметри фільтрації:</strong> <code>cause_num</code> (номер справи), <code>judgment_code</code> (код судді), <code>category_code</code> (категорія).</p>
                    <p><strong>Сортування:</strong> <code>sort_by=doc_id|adjudication_date|court_name</code>, <code>order=asc|desc</code>.</p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Pagination */}
            <section id="pagination">
              <h2 className="text-2xl font-bold mb-4">Пагінація</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">
                    API підтримує два методи пагінації:
                  </p>
                  <ul className="list-disc list-inside space-y-2 mb-4">
                    <li><strong>Offset пагінація:</strong> використовуйте <code>page</code> та <code>limit</code> (обмеження 10 000 записів).</li>
                    <li><strong>Курсорна пагінація (рекомендована):</strong> передавайте значення <code>cursor</code> з попередньої відповіді, не обмежена 10 000 записів.</li>
                  </ul>
                  <CodeSnippet code={`# Приклад відповіді з курсором
{
  "page": null,
  "limit": 50,
  "total": 12345,
  "next_cursor": "eyJpZCI6MTIzNDU2Nzg5fQ",
  "data": [...]
}`} />
                </div>
              </Card>
            </section>

            {/* Filters */}
            <section id="filters">
              <h2 className="text-2xl font-bold mb-4">Фільтрація</h2>
              <Card>
                <div className="p-6">
                  <p className="mb-4 text-gray-700">Доступні наступні параметри фільтрації (залежно від ендпоїнту):</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-3"><code>debtor_code</code><p className="text-xs text-gray-600">Код боржника (exact match)</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>debtor_birthdate</code><p className="text-xs text-gray-600">Дата народження (YYYY-MM-DD)</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>debtor_name</code><p class="text-xs text-gray-600">ПІБ боржника (повнотекстовий)</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>vp_ordernum</code><p className="text-xs text-gray-600">Номер виконавчого провадження</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>dvs_code</code><p className="text-xs text-gray-600">Код ДВС</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>cause_num</code><p className="text-xs text-gray-600">Номер судової справи</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>judgment_code</code><p className="text-xs text-gray-600">Код судді</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><code>category_code</code><p className="text-xs text-gray-600">Код категорії справи</p></div>
                  </div>
                </div>
              </Card>
            </section>

            {/* Error Codes */}
            <section id="errors">
              <h2 className="text-2xl font-bold mb-4">Коди помилок</h2>
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Код</th>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Помилка</th>
                        <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Опис</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr><td className="px-6 py-3 font-mono text-sm">400</td><td className="px-6 py-3">bad_request</td><td className="px-6 py-3 text-sm">Невалідні параметри запиту</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">401</td><td className="px-6 py-3">invalid_token</td><td className="px-6 py-3 text-sm">Відсутній/прострочений токен</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">403</td><td className="px-6 py-3">insufficient_scope</td><td className="px-6 py-3 text-sm">Недостатні права доступу</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">404</td><td className="px-6 py-3">not_found</td><td className="px-6 py-3 text-sm">Сутність відсутня</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">429</td><td className="px-6 py-3">rate_limited</td><td className="px-6 py-3 text-sm">Перевищено ліміт запитів</td></tr>
                      <tr><td className="px-6 py-3 font-mono text-sm">500</td><td className="px-6 py-3">internal_error</td><td className="px-6 py-3 text-sm">Внутрішня помилка сервера</td></tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </section>

            {/* Roles */}
            <section id="roles">
              <h2 className="text-2xl font-bold mb-4">Ролі користувачів</h2>
              <Card>
                <div className="p-6 space-y-3">
                  {Object.entries(USER_ROLES).map(([role, description]) => (
                    <div key={role} className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                      <h3 className="font-semibold text-gray-900 mb-1">{role}</h3>
                      <p className="text-sm text-gray-600">{description}</p>
                    </div>
                  ))}
                </div>
              </Card>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Docs;